import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-context';
import { useLocale } from '../i18n/locale-context';
import { createSavedSearch, saveResultsSearch } from '../lib/saved-search-client';
import { trackSavedSearchActivation } from '../lib/tracking';
import { readPendingSavedSearch, clearPendingSavedSearch } from '../lib/pending-saved-search';
import { uuid } from '../lib/uuid';

/**
 * Completes the guest → register hand-off for "Сохранить поиск". When a guest
 * clicks Save, `SaveSearchButton` stashes the intended search and routes them to
 * /register. Once auth flips to `authed` (post-register OR post-login), this
 * run-once effect replays that stash as a real saved search, then invalidates
 * the shared ['saved-searches'] cache so the sidebar/dashboard pick it up.
 *
 * Mounted once at the app root (inside Locale/Auth/Query providers) so it fires
 * regardless of which route the user lands on after auth. Renders nothing.
 *
 * Double-creation is guarded two ways: the stash is cleared *before* the network
 * call, and a ref latches so the effect can't re-run within a session (incl.
 * StrictMode's double-invoke).
 */
export function PendingSavedSearchSync(): null {
  const { status } = useAuth();
  const { t, locale } = useLocale();
  const qc = useQueryClient();
  const handledRef = useRef(false);

  useEffect(() => {
    if (status !== 'authed' || handledRef.current) return;
    const pending = readPendingSavedSearch();
    if (!pending) return;

    // Latch + clear up front so a re-render / StrictMode replay can't double-create.
    handledRef.current = true;
    clearPendingSavedSearch();

    // The /results card: the reader's one `results` search. Not an activation
    // (that event means a LOTS search — the alerts the funnel counts), so no
    // Meta/Pixel twin here.
    if (pending.results) {
      // The name is fixed server-side («Результаты» / «Natijalar»), picked by
      // the reader's language.
      void saveResultsSearch({
        criteria: pending.results.criteria,
        lang: locale,
      })
        .then(() => qc.invalidateQueries({ queryKey: ['results-search'] }))
        .then(() => window.dispatchEvent(new CustomEvent('tenders:saved-search')))
        .catch(() => {
          // Best-effort, as below — the card's button is still there.
        });
      return;
    }

    const metaEventId = uuid();
    trackSavedSearchActivation(metaEventId);
    void createSavedSearch({
      name: pending.name?.trim() || t('saved.defaultName'),
      regionCode: pending.regionCode,
      categorySlug: pending.categorySlug,
      ikpuCode: pending.ikpuCode,
      nicheSlug: pending.nicheSlug,
      keyword: pending.keyword,
      scope: pending.scope,
      includeSupply: pending.includeSupply,
      metaEventId,
    })
      .then(() => qc.invalidateQueries({ queryKey: ['saved-searches'] }))
      .catch(() => {
        // Best-effort — the stash is already cleared; the user can re-save
        // manually from the (now enabled) catalog button.
      });
  }, [status, qc, t]);

  return null;
}
